import { useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';

// Lädt eine OSM-Ebene des Backends (Hydranten, POIs) kachelweise für den sichtbaren Ausschnitt.
// Geladene Kacheln bleiben für die ganze Sitzung erhalten, auch beim Aus- und Einschalten;
// angefragt werden nur Kacheln, die noch fehlen.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Kachelgröße in Grad, muss zu OSM_TILE im Backend passen
const TILE = 0.02;
const MAX_TILES_PER_REQUEST = 100;
const DEBOUNCE_MS = 250;

// Kacheln, die den Ausschnitt abdecken, als "y_x" (Index = abgerundet Koordinate / TILE)
const tileKeysFor = (bounds) => {
  const keys = [];
  for (let y = Math.floor(bounds.getSouth() / TILE); y <= Math.floor(bounds.getNorth() / TILE); y++) {
    for (let x = Math.floor(bounds.getWest() / TILE); x <= Math.floor(bounds.getEast() / TILE); x++) {
      keys.push(`${y}_${x}`);
    }
  }
  return keys;
};

// filter: optionale Auswahl (z.B. POI-Kategorien); filterKey ändert sich, wenn sich der Filter ändert
export const useOsmTiles = ({ layer, enabled, minZoom, maxItems, filter, filterKey = '', onStatusChange }) => {
  const map = useMap();
  const [items, setItems] = useState([]);
  const tilesRef = useRef(new Map());
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const statusRef = useRef(onStatusChange);
  statusRef.current = onStatusChange;

  const report = useCallback((status) => statusRef.current?.(status), []);

  const isActive = useCallback(
    () => enabledRef.current && map.getZoom() >= minZoom,
    [map, minZoom]
  );

  // Einträge im Ausschnitt aus den geladenen Kacheln anzeigen
  const showVisible = useCallback(() => {
    const bounds = map.getBounds();
    const visible = [];
    for (const key of tileKeysFor(bounds)) {
      for (const item of tilesRef.current.get(key) || []) {
        if (bounds.contains([item.lat, item.lon]) && (!filterRef.current || filterRef.current(item))) {
          visible.push(item);
        }
      }
    }
    setItems(visible.slice(0, maxItems));
    report({
      state: pendingRef.current.size > 0 ? 'loading' : 'ok',
      count: visible.length,
      truncated: visible.length > maxItems,
    });
  }, [map, maxItems, report]);

  // Fehlende Kacheln des aktuellen Ausschnitts beim Backend anfragen
  const fetchMissing = useCallback(async () => {
    if (!isActive()) return;
    const missing = tileKeysFor(map.getBounds())
      .filter(key => !tilesRef.current.has(key) && !pendingRef.current.has(key))
      .slice(0, MAX_TILES_PER_REQUEST);
    if (missing.length === 0) return;

    missing.forEach(key => pendingRef.current.add(key));
    showVisible();
    try {
      const response = await fetch(`${API}/osm/${layer}?tiles=${missing.join(',')}`, {
        signal: abortRef.current?.signal,
      });
      if (!response.ok) {
        // Das Backend liefert einen lesbaren Grund (z.B. Datenquelle überlastet)
        const body = await response.json().catch(() => ({}));
        const error = new Error(`HTTP ${response.status}`);
        error.detail = typeof body.detail === 'string' ? body.detail : null;
        throw error;
      }
      const data = await response.json();
      Object.entries(data.tiles || {}).forEach(([key, elements]) => tilesRef.current.set(key, elements));
      missing.forEach(key => pendingRef.current.delete(key));
      if (isActive()) showVisible();
    } catch (error) {
      missing.forEach(key => pendingRef.current.delete(key));
      if (error.name === 'AbortError') return;
      console.error(`OSM-Ebene "${layer}" konnte nicht geladen werden:`, error);
      if (isActive()) report({ state: 'error', detail: error.detail, retry: () => fetchMissingRef.current() });
    }
  }, [map, layer, isActive, showVisible, report]);
  const fetchMissingRef = useRef(fetchMissing);
  fetchMissingRef.current = fetchMissing;

  const update = useCallback(() => {
    clearTimeout(timerRef.current);
    if (!enabledRef.current) {
      setItems([]);
      report(null);
      return;
    }
    if (map.getZoom() < minZoom) {
      setItems([]);
      report({ state: 'zoom' });
      return;
    }
    // Bekanntes sofort zeigen, Fehlendes nach kurzer Pause laden (nicht bei jedem Zwischenschritt)
    showVisible();
    timerRef.current = setTimeout(fetchMissing, DEBOUNCE_MS);
  }, [map, minZoom, report, showVisible, fetchMissing]);

  // Handler einmalig registrieren: useMapEvents meldet sich bei jedem Render neu an und
  // verpasst dabei moveend-Ereignisse aus setView (z.B. nach der Adresssuche)
  const updateRef = useRef(update);
  updateRef.current = update;
  useEffect(() => {
    const handleMoveEnd = () => updateRef.current();
    map.on('moveend', handleMoveEnd);
    return () => map.off('moveend', handleMoveEnd);
  }, [map]);

  useEffect(() => {
    update();
  }, [enabled, filterKey, update]);

  // Laufende Anfragen beim Entfernen der Karte abbrechen
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    return () => {
      clearTimeout(timerRef.current);
      controller.abort();
    };
  }, []);

  return items;
};
